/* =============================================================================
 * Pacote basico de navegacao.
 *
 * E' o pacote que faz o app ABRIR sem internet. Sem ele os outros nao adiantam:
 * o boot para no shell (app/main.js), e nenhuma pagina chega a renderizar.
 *
 * O QUE ELE GUARDA, e por que cada peca esta' aqui:
 *
 *   shell ......... a sessao inteira, incluindo o IdentificadorConexao, que
 *                   entra no hash de TODA url de imagem. Sem ele o app ate' abre,
 *                   mas todo escudo quebra - com 200 no console e nenhuma pista.
 *   vinculos ...... clubes e atletas da pessoa. E' o que decide os atalhos de
 *                   gestao (sessao/capacidades.js); sem eles quem dirige um clube
 *                   abriria o app offline como torcedor.
 *   competicoes ... a lista publica. E' a home e e' /competicoes: sem ela o app
 *                   abre numa tela vazia, que se le como "quebrou".
 *   clubes ........ a lista publica de clubes, o quarto destino da navegacao.
 *
 * DUAS PORTAS PARA O SHELL, e nao uma. Logado, o download usa a
 * ObterDadosShellUsuarioOffline (a mesma do iPrisma: ela ignora o cache redis de
 * proposito, porque este dado vai ficar dias no aparelho). Deslogado, usa a
 * propria ObterInformacoesShellUsuarioAnonimo - o Futigol e' publico, e o
 * visitante tem tanto direito ao offline quanto quem tem conta.
 *
 * IdPlataforma ZERO: o Futigol nao tem ambientes nem navegacao por plataforma -
 * e' a mesma forma com que app/main.js ja' chama o ObterInformacoesShellUsuario.
 * Pelo mesmo motivo os tres `Buscar*` vao desligados: ambientes, grupos e
 * aplicativos sao do iPrisma e aqui so' engordariam o pacote.
 * ========================================================================== */

import { executarFuncionalidade } from '@shared/api/ServicoAplicacao.js';
import { sessao } from '@shared/sessao/sessao.js';
import { urlImagem } from '@shared/lib/arquivos.js';
import { paraInteiro, paraLista, paraTexto } from '@shared/lib/valores.js';
import * as offline from './offline.js';

export const TIPO = 'navegacao';
//Escopo unico: existe um so' por contexto, entao o id e' fixo.
const ID = 1;
const NOME_DATASET = 'navegacao';

//O dataset e' consultado a cada troca de tela offline; depois da primeira
//leitura ele fica em memoria.
let datasetMemoria = null;

async function dataset() {
    if (datasetMemoria) return datasetMemoria;
    datasetMemoria = await offline.obterDataset(NOME_DATASET);
    return datasetMemoria;
}

export function registrarPacoteNavegacao() {
    offline.registrarTipoPacote({
        tipo: TIPO,
        titulo: 'Navegação básica',
        descricao: 'Sua sessão, os clubes que você administra e as listas de competições e clubes. É o que permite abrir o app sem internet.',
        icone: 'casa',
        ordem: 1,
        escopo: 'unico',
        essencial: true,
        rota: '/',
        //Escopo unico: o id nao vem de fora - existe um pacote so' por contexto,
        //e a page nem precisa saber qual e' o numero dele.
        baixar: (id, opcoes) => baixar(opcoes),
        aoRemover: () => {
            datasetMemoria = null;
        },
    });

    const disponivel = () => offline.pacoteDisponivel(TIPO, ID);
    const dataPacote = () => offline.obterPacote(TIPO, ID)?.dataDownload ?? null;

    //O shell guardado e' entregue ao MESMO `aplicarShell` que trata a resposta
    //online - por isso o pacote grava o payload cru, sem conversao nenhuma.
    offline.registrarResolvedor('ObterInformacoesShellUsuario', {
        podeResolver: () => disponivel() && !!sessao.usuario,
        dataPacote,
        resolver: async () => (await dataset())?.shell ?? null,
    });

    offline.registrarResolvedor('ObterInformacoesShellUsuarioAnonimo', {
        podeResolver: () => disponivel(),
        dataPacote,
        //Quem baixou o pacote logado nao tem o payload anonimo, e vice-versa: o
        //shell do usuario serve aos dois casos porque carrega os mesmos campos.
        resolver: async () => {
            const d = await dataset();
            return d?.shellAnonimo ?? d?.shell ?? null;
        },
    });

    offline.registrarResolvedor('ObterTabUsuarioClubes', {
        podeResolver: (func) => disponivel() && paraInteiro(func.IdUsuario) > 0,
        dataPacote,
        resolver: async () => (await dataset())?.meusClubes ?? null,
    });

    offline.registrarResolvedor('ObterTabUsuarioAtletas', {
        podeResolver: (func) => disponivel() && paraInteiro(func.IdUsuario) > 0,
        dataPacote,
        resolver: async () => (await dataset())?.meusAtletas ?? null,
    });

    offline.registrarResolvedor('ObterTabCompeticoesFutigol', {
        podeResolver: () => disponivel(),
        dataPacote,
        resolver: async () => (await dataset())?.competicoes ?? null,
    });

    offline.registrarResolvedor('ObterTabClubesFutigol', {
        podeResolver: () => disponivel(),
        dataPacote,
        resolver: async () => (await dataset())?.clubes ?? null,
    });
}

/* ---- download ----------------------------------------------------------- */

async function baixar({ aoProgredir = () => {}, controle = {} } = {}) {
    const usuario = sessao.usuario;
    const novo = {};

    aoProgredir({ etapa: 'dados', descricao: 'Obtendo os dados da sua sessão…', percentual: 5 });

    if (usuario) {
        const r = await executarFuncionalidade({
            NomeFuncionalidade: 'ObterDadosShellUsuarioOffline',
            IdUsuario: paraInteiro(usuario.IdUsuario),
            IdPlataforma: 0,
            BuscarAmbientesOpcoes: false,
            BuscarGruposAmbientes: false,
            BuscarAplicativos: false,
        });

        //Sem Usuario o payload nao serve para nada: o resolvedor devolveria um
        //objeto vazio e o app abriria sem sessao, dando a impressao de que o
        //pacote existe. Melhor falhar aqui, visivel.
        if (!r?.Usuario) throw new Error('O servidor não devolveu os dados da conta. Confira se a versão da API tem a funcionalidade ObterDadosShellUsuarioOffline.');
        novo.shell = r;

        aoProgredir({ etapa: 'dados', descricao: 'Obtendo seus vínculos…', percentual: 15 });
        novo.meusClubes = await executarFuncionalidade({ NomeFuncionalidade: 'ObterTabUsuarioClubes', IdUsuario: paraInteiro(usuario.IdUsuario) });
        novo.meusAtletas = await executarFuncionalidade({ NomeFuncionalidade: 'ObterTabUsuarioAtletas', IdUsuario: paraInteiro(usuario.IdUsuario) });
    } else {
        novo.shellAnonimo = await executarFuncionalidade({ NomeFuncionalidade: 'ObterInformacoesShellUsuarioAnonimo' }, { semToken: true });
    }

    aoProgredir({ etapa: 'dados', descricao: 'Obtendo competições e clubes…', percentual: 25 });
    novo.competicoes = await executarFuncionalidade({ NomeFuncionalidade: 'ObterTabCompeticoesFutigol' });
    novo.clubes = await executarFuncionalidade({ NomeFuncionalidade: 'ObterTabClubesFutigol' });

    const competicoes = paraLista(novo.competicoes?.Competicoes);
    const clubes = paraLista(novo.clubes?.Clubes);
    novo.resumo = { competicoes: competicoes.length, clubes: clubes.length, comConta: !!usuario };

    const imagens = urlsDeImagens(usuario, competicoes, clubes);
    const { baixadas, bytes } = await offline.baixarImagens(imagens, {
        controle,
        aoProgredir,
        inicial: 35,
        final: 95,
        descricao: 'Baixando escudos e imagens…',
    });

    aoProgredir({ etapa: 'gravando', descricao: 'Gravando no aparelho…', percentual: 97 });
    datasetMemoria = novo;

    //Gravacao no IndexedDB pode falhar por cota. Registrar o pacote como
    //concluido nesse caso seria prometer conteudo que nao existe.
    if (!(await offline.gravarDataset(NOME_DATASET, novo))) {
        throw new Error('Não foi possível gravar o conteúdo no aparelho. Verifique o espaço disponível.');
    }

    const pacote = await offline.salvarPacote({
        tipo: TIPO,
        id: ID,
        titulo: 'Navegação básica',
        subtitulo: usuario ? 'Sessão e listas' : 'Listas públicas',
        icone: 'casa',
        rota: '/',
        nroItens: competicoes.length + clubes.length,
        nroImagens: baixadas,
        bytes,
        situacao: controle.cancelado ? 'parcial' : 'concluido',
        datasets: [NOME_DATASET],
        imagens,
        resumo: novo.resumo,
        dataDownload: new Date(),
    });

    aoProgredir({ etapa: 'concluido', descricao: 'Navegação disponível offline.', percentual: 100 });
    return pacote;
}

/**
 * As MESMAS urls que a tela vai pedir.
 *
 * Qualquer diferenca aqui (um `view=true` a mais, outro identificador) e o cache
 * nao seria encontrado na hora de exibir - por isso passa pelo `urlImagem` de
 * lib/arquivos.js, o mesmo que o componente `escudo` usa.
 */
function urlsDeImagens(usuario, competicoes, clubes) {
    const caminhos = [paraTexto(usuario?.Imagem), ...competicoes.map((c) => paraTexto(c?.Imagem)), ...clubes.map((c) => paraTexto(c?.Logotipo))];

    return [...new Set(caminhos.filter(Boolean).map((caminho) => urlImagem(caminho)))].filter(Boolean);
}
